<template>
  <app-full-wrapper
    @click="wrapperClick"
    ref="wrapper"
    v-model="showWrapper"
    class="app-sidebar-wrapper"
    :destroyOnClose="$attrs.destroyOnClose"
  >
    <aside
      v-bind="$attrs"
      ref="sidebar"
      class="app-sidebar"
      :class="{ 'full-width': fullWidth, right: direction === 'right' }"
    >
      <slot />
    </aside>
  </app-full-wrapper>
</template>

<script>
const STATE_CLOSED = 0
const STATE_CLOSING = 1
const STATE_OPENING = 2
const STATE_OPENED = 3

export default {
  name: 'AppSidebar',

  data() {
    return {
      beforeEachFn: undefined,
      state: STATE_CLOSED,
      showWrapper: false,
    }
  },

  computed: {
    isVisible() {
      return this.showWrapper
    },

    active() {
      return this.$attrs.active ?? this.modelValue
    },
  },

  props: {
    onBeforeBack: {
      type: Function,
      required: false,
    },

    closeOnWrapperClick: {
      type: Boolean,
      default: true,
    },

    closeOnBack: {
      type: Boolean,
      default: true,
    },

    fullWidth: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: Boolean,
    },

    direction: {
      type: String,
      default: 'left',
    },
  },

  watch: {
    active: {
      immediate: true,
      handler(value) {
        switch (value) {
          case true:
            this.open()
            break

          case false:
            this.close()
            break
        }
      },
    },
  },

  unmounted() {
    this.removeBeforeEachHook()
  },

  beforeCreate() {
    this.$on('opening', () => {
      this.addBeforeEachHook()
      this.showWrapper = true
      this.$emit('update:modelValue', true)
    }).$on('closing', () => {
      this.removeBeforeEachHook()
      this.showWrapper = false
      this.$emit('update:modelValue', false)
    })
  },

  mounted() {
    const sidebarWrapper = this.$el

    sidebarWrapper.addEventListener('transitionend', event => {
      if (event.propertyName !== 'transform') return

      switch (this.state) {
        case STATE_OPENING:
          this.state = STATE_OPENED
          this.$emit('opened', this)
          break

        case STATE_CLOSING:
          this.state = STATE_CLOSED
          this.$emit('closed', this)
          break
      }
    })
  },

  methods: {
    async addBeforeEachHook() {
      await this.removeBeforeEachHook()

      if (this.closeOnBack === true) {
        this.beforeEachFn = this.$router.beforeEach(async (to, from, next) => {
          const currentWrapperId = app.getCurrentFullWrapper()
          const componentWrapperId = this.$refs.wrapper?._uid

          if (
            currentWrapperId === componentWrapperId &&
            this.isVisible === true
          ) {
            const { onBeforeBack } = this

            if (typeof onBeforeBack === 'function') {
              const result = await onBeforeBack(this)

              if (result !== false) this.close()
            } else {
              this.close()
            }

            if (!app.isGoToScreen) {
              next(false)
              return
            }
          }

          next()
        })
      }
    },

    async removeBeforeEachHook() {
      if (typeof this.beforeEachFn === 'function') await this.beforeEachFn()

      this.beforeEachFn = undefined
    },

    wrapperClick() {
      if (this.closeOnWrapperClick) this.close()
    },

    async open() {
      return new Promise((resolve, reject) => {
        if ([STATE_OPENING, STATE_OPENED].includes(this.state)) return resolve()

        this.$once('opened', resolve)

        this.state = STATE_OPENING

        this.$emit('opening', this)
      })
    },

    async close() {
      return new Promise((resolve, reject) => {
        if ([STATE_CLOSING, STATE_CLOSED].includes(this.state)) return resolve()

        this.$once('closed', resolve)

        this.state = STATE_CLOSING

        this.$emit('closing', this)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/styles/settings.scss' as *;

.app-sidebar {
  display: flex;
  flex-direction: column;
  background-color: #fff;
  max-width: 75vw;
  min-width: fit-content;
  width: fit-content;
  min-height: 100vh;
  min-height: 100dvh;
  transition: transform $defaultTransitionDuration;
  transform: translateX(-101%);

  &.full-width {
    max-width: 100vw;
    width: 100%;
  }

  &.right {
    transform: translateX(101%);
    margin-left: auto;
  }
}

.app-sidebar-wrapper {
  &.opened,
  &.opening {
    > .app-sidebar {
      transform: translateX(0);
    }
  }
}
</style>
